import { EventEmitter, Injectable } from '@angular/core';
import { Router, ActivatedRoute, NavigationStart, NavigationError } from '@angular/router';
import { filter } from 'rxjs/operators';

import { Logger } from './logger';
import { RouterDataService } from './router-data.service';

export interface RouterStackGoOptions {
    push?: boolean;
}

export interface RouterStackInternalGoOptions extends RouterStackGoOptions {
    push: boolean;
    isBack: boolean;
}

export class NavigationEventState {
    private _isBackClicked: boolean;
    get isBackClicked() {
        return this._isBackClicked;
    }

    private _url: string;
    get url() {
        return this._url;
    }

    constructor(isBackClicked: boolean, url: string) {
        this._isBackClicked = isBackClicked;
        this._url = url;
    }
}

@Injectable({
    providedIn: 'root',
})
export class RouterStackService {
    // events

    navigationChange: EventEmitter<NavigationEventState> = new EventEmitter<NavigationEventState>();

    // constructor

    constructor(
        private activatedRoute: ActivatedRoute,
        private logger: Logger,
        private router: Router,
        private routerDataService: RouterDataService,
    ) {
        this.router.events
            .pipe(filter((event) => event instanceof NavigationStart))
            .subscribe((event: NavigationStart) => {
                this.logger.debug(event);

                if (this.stateStack.length === 0) {
                    this._lastState = null;
                }
            });

        this.router.events.pipe(filter((event) => event instanceof NavigationError)).subscribe((e) => {
            this.logger.error(e);
        });
    }

    // data members

    private _stateStack: Array<RouterState> = new Array<RouterState>();
    get stateStack() {
        return this._stateStack;
    }

    private _lastState: RouterState;
    get lastState(): RouterState {
        return this._lastState;
    }

    private _url: string;
    get url() {
        if (this.router) return this.router.url;

        return '';
    }

    // public functions

    async go(url: string, params?: any, state?: any, options?: RouterStackGoOptions) {
        const push = options?.push ?? true;
        return this.internalGo(url, params, state, { push, isBack: false });
    }

    async goAndReset(url: string, params?: any, state?: any) {
        const navigated = await this.internalGo(url, params, state, { push: false, isBack: false });
        if (navigated) {
            this._stateStack = [];
        }

        return navigated;
    }

    async back(): Promise<boolean> {
        let popState = this.stateStack[this.stateStack.length - 1];
        if (!popState) {
            return false;
        }
        const isNavigated = await this.internalGo(popState.from, popState.params, popState.state, {
            push: false,
            isBack: true,
        });
        if (isNavigated) {
            this.stateStack.pop();
        }

        return isNavigated;
    }

    async home(): Promise<boolean> {
        const stackItemsCount = this.stateStack.length;
        let popState = this.stateStack[this.stateStack.length - 1];
        if (stackItemsCount == 0) return Promise.resolve(true);

        const isNavigated = await this.internalGo('/', popState.params, popState.state, {
            push: false,
            isBack: true,
        });

        if (isNavigated) {
            // Rimuovi lo stato dallo stack di stato solo se la navigazione ha avuto successo
            this.stateStack.pop();
        }

        return isNavigated;
    }

    // private functions

    private async internalGo(
        url: string,
        params: any,
        state: any,
        options: RouterStackInternalGoOptions,
    ): Promise<boolean> {
        this.navigationChange.emit(new NavigationEventState(options.push, url));

        // Se si naviga indietro verso il client Html5, redirigi al menu perché non è possibile recuperare gli $stateParams.
        if (!url.startsWith('/new') && options.isBack) {
            url = '/menu';
        }

        let newState = this.buildRouterState(url, params, state);
        this._lastState = newState;

        if (options.push) {
            this.stateStack.push(newState);
        }

        // TODO: 34.13 NEL CASO DI BACK QUESTI SONO I PARAMETRI DELLA PAGINA CORRENTE???
        // NEL CASO CI SIANO DATI NELLO STATE USA QUELLI COME WORKAROUND.
        if (options.isBack && newState.state && newState.state.restoreRouterDataOnBack) {
            this.routerDataService.setData(newState.state.restoreRouterDataOnBack);
        } else {
            this.routerDataService.setData({ ...params });
        }

        return this.router.navigate([url], { relativeTo: this.activatedRoute.parent }).catch((error) => {
            this.logger.error(error);

            return false;
        });
    }

    private buildRouterState(to: string, params, state): RouterState {
        return new RouterState(this.router.url, to, params, state);
    }
}

class RouterState {
    // data members

    private _from: string;
    get from() {
        return this._from;
    }

    private _to: string;
    get to() {
        return this._to;
    }

    private _params: any;
    get params() {
        return this._params;
    }

    private _state: any;
    get state() {
        return this._state;
    }

    // constructor

    constructor(from: string, to: string, params: any, state: any) {
        this._from = from;
        this._to = to;
        this._params = params;
        this._state = state;
    }
}
